import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import * as api from '../api/client';
import type { GatherAudience, GatherGoal } from './gather/GatherCards';

// ═══ Types ═══

interface ConceptNode {
  id: string;
  label: string;
  sub: string;
  cat: string;
  level: number;
  x: number;
  y: number;
  notes: string;
}

interface ConceptLink {
  from: string;
  to: string;
  relation: string;
}

interface ConceptCloudData {
  title: string;
  nodes: ConceptNode[];
  links: ConceptLink[];
  _version?: number;
  _updated_by?: string;
}

interface ConceptCloudProps {
  deckId: string;
  deckTitle?: string;
  audienceInfo: GatherAudience | null;
  goalInfo: GatherGoal | null;
  keyMessages: string[] | null;
  onClose?: () => void;
  contextVersion?: number;
  /** Parent-prefetched context items (App.tsx already loaded these
   *  in selectDeck's Promise.all). When provided we skip the initial
   *  listContext() — eliminates a 2-3s duplicate fetch on every
   *  deck-open hot path. The contextVersion-triggered re-fetch still
   *  uses its own listContext call since by then the parent's snapshot
   *  is stale. */
  prefetchedContext?: { key: string; content: string }[];
  /** Deck id for which prefetchedContext reflects a completed parent
   *  fetch. Lets ConceptCloud wait when the parent is still loading
   *  (prefetchedContext === [] AND contextHydratedDeckId !== deckId)
   *  — without this signal, fresh-mount sees the cleared empty array
   *  and races the parent's Promise.all, ending up firing its own
   *  listContext anyway. */
  contextHydratedDeckId?: string | null;
}

// ═══ Constants ═══

const CAT: Record<string, { c: string; label: string; icon: string }> = {
  goal:     { c: '#e67e22', label: 'Goal',           icon: '🎯' },
  audience: { c: '#3b82f6', label: 'Audience',       icon: '👥' },
  message:  { c: '#10b981', label: 'Key Message',    icon: '💬' },
  differ:   { c: '#8b5cf6', label: 'Differentiator', icon: '⚡' },
  pain:     { c: '#ef4444', label: 'Pain Point',     icon: '🔥' },
  context:  { c: '#06b6d4', label: 'Context',        icon: '📋' },
};

const NODE_SIZES = [140, 96, 68]; // level 1, 2, 3
const nodeSize = (lv: number) => NODE_SIZES[(lv || 1) - 1] || 68;

const FLOAT_ANIMS = [
  'ccFloatA 4s ease-in-out infinite',
  'ccFloatB 5s ease-in-out infinite',
  'ccFloatC 3.5s ease-in-out infinite',
];

let nextIdCounter = 100;
const genId = () => 'n' + (nextIdCounter++);

function hexToRgb(h: string) {
  h = h.replace('#', '');
  return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)].join(',');
}

// ═══ Auto BFS Layout ═══

function autoLayout(data: ConceptCloudData): void {
  const { nodes, links } = data;
  if (!nodes.length) return;

  const roots = nodes.filter(n => n.level === 1);
  if (!roots.length) {
    // Fallback: pick most-connected node as root
    const conn: Record<string, number> = {};
    nodes.forEach(n => (conn[n.id] = 0));
    links.forEach(l => { conn[l.from] = (conn[l.from] || 0) + 1; conn[l.to] = (conn[l.to] || 0) + 1; });
    const topId = Object.entries(conn).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (topId) { const n = nodes.find(nd => nd.id === topId); if (n) roots.push(n); }
  }

  const adj: Record<string, string[]> = {};
  nodes.forEach(n => (adj[n.id] = []));
  links.forEach(l => { adj[l.from]?.push(l.to); adj[l.to]?.push(l.from); });

  const depth: Record<string, number> = {};
  const vis = new Set<string>();
  const q: string[] = [];
  roots.forEach(r => { depth[r.id] = 0; vis.add(r.id); q.push(r.id); });
  while (q.length) {
    const c = q.shift()!;
    for (const nb of (adj[c] || [])) {
      if (!vis.has(nb)) { vis.add(nb); depth[nb] = depth[c] + 1; q.push(nb); }
    }
  }
  nodes.forEach(n => { if (depth[n.id] === undefined) depth[n.id] = n.level; });

  const layers: Record<number, ConceptNode[]> = {};
  nodes.forEach(n => { const d = depth[n.id]; if (!layers[d]) layers[d] = []; layers[d].push(n); });

  const catOrd: Record<string, number> = { goal: 0, audience: 1, differ: 2, message: 3, pain: 4, context: 5 };
  Object.values(layers).forEach(l => l.sort((a, b) => (catOrd[a.cat] || 9) - (catOrd[b.cat] || 9)));

  const keys = Object.keys(layers).map(Number).sort((a, b) => a - b);
  const yS = 0.10, yE = 0.82;
  const yStep = keys.length > 1 ? (yE - yS) / (keys.length - 1) : 0;

  keys.forEach((d, li) => {
    const l = layers[d];
    const n = l.length;
    const xS = 0.06, xE = 0.94;
    const xStep = n > 1 ? (xE - xS) / (n - 1) : 0;
    l.forEach((nd, i) => {
      nd.x = n === 1 ? 0.5 : xS + xStep * i;
      nd.y = yS + yStep * li;
      // Slight jitter
      nd.x += ((nd.id.charCodeAt(nd.id.length - 1) % 5) - 2) * 0.01;
      nd.x = Math.max(0.07, Math.min(0.93, nd.x));
      nd.y = Math.max(0.10, Math.min(0.88, nd.y));
    });
  });
}

// ═══ Derive initial data from gather props ═══

function deriveFromProps(
  title: string,
  audience: GatherAudience | null,
  goal: GatherGoal | null,
  messages: string[] | null,
): ConceptCloudData {
  const nodes: ConceptNode[] = [];
  const links: ConceptLink[] = [];

  // Only create a root node if there's real data to connect to
  const hasData = audience || goal || (messages && messages.length > 0);

  if (hasData) {
    nodes.push({
      id: 'n1', label: title || 'Pitch', sub: 'Core Objective',
      cat: 'goal', level: 1, x: 0.5, y: 0.3, notes: '',
    });
  }

  if (audience) {
    const id = genId();
    nodes.push({
      id, label: audience.audience || 'Target Audience', sub: audience.company_type || '',
      cat: 'audience', level: 2, x: 0.25, y: 0.5, notes: audience.pain_points || '',
    });
    links.push({ from: 'n1', to: id, relation: 'targets' });

    if (audience.pain_points) {
      const pid = genId();
      nodes.push({
        id: pid, label: 'Pain Points', sub: audience.pain_points.slice(0, 40),
        cat: 'pain', level: 3, x: 0.15, y: 0.7, notes: audience.pain_points,
      });
      links.push({ from: id, to: pid, relation: 'suffers from' });
    }
  }

  if (goal) {
    const id = genId();
    nodes.push({
      id, label: goal.outcome || 'Goal', sub: goal.duration || '',
      cat: 'goal', level: 2, x: 0.75, y: 0.5, notes: '',
    });
    links.push({ from: 'n1', to: id, relation: 'aims for' });
  }

  if (messages && messages.length > 0) {
    messages.forEach((msg, i) => {
      const id = genId();
      nodes.push({
        id, label: msg.slice(0, 30), sub: '',
        cat: 'message', level: 3, x: 0.3 + i * 0.2, y: 0.75, notes: msg,
      });
      links.push({ from: 'n1', to: id, relation: 'conveys' });
    });
  }

  const data: ConceptCloudData = { title: title || 'Pitch Brief', nodes, links };
  autoLayout(data);
  return data;
}

// ═══ Merge incoming gather data into existing cloud ═══

function mergeGatherData(
  existing: ConceptCloudData,
  audience: GatherAudience | null,
  goal: GatherGoal | null,
  messages: string[] | null,
): ConceptCloudData {
  // Only merge if we detect new data that isn't already represented
  // If cloud is empty or only has the default root, derive fresh
  const isDefault = existing.nodes.length === 0 ||
    (existing.nodes.length === 1 && existing.nodes[0].id === 'n1');
  if (isDefault) {
    return deriveFromProps(existing.title, audience, goal, messages);
  }
  // Otherwise, preserve existing cloud — user edits take priority
  return existing;
}

// ═══ Merge remote (agent) data into local cloud ═══

function mergeRemoteData(local: ConceptCloudData, remote: ConceptCloudData): ConceptCloudData {
  const remoteVer = remote._version ?? 0;
  const localVer = local._version ?? 0;

  // Only merge if remote is newer
  if (remoteVer <= localVer) return local;

  const localNodeMap = new Map(local.nodes.map(n => [n.id, n]));
  const mergedNodes = [...local.nodes];

  // Add new nodes from remote; preserve local positions for existing ones
  for (const rn of remote.nodes) {
    const existing = localNodeMap.get(rn.id);
    if (!existing) {
      mergedNodes.push(rn);
    }
    // If node already exists locally, keep local version (preserves user x/y/edits)
  }

  // Links: union by (from, to)
  const linkKey = (l: ConceptLink) => `${l.from}::${l.to}`;
  const existingLinks = new Set(local.links.map(linkKey));
  const mergedLinks = [...local.links];
  for (const rl of remote.links) {
    if (!existingLinks.has(linkKey(rl))) {
      mergedLinks.push(rl);
      existingLinks.add(linkKey(rl));
    }
  }

  // Auto-layout only the new nodes (ones not in local)
  const newNodeIds = new Set(remote.nodes.filter(rn => !localNodeMap.has(rn.id)).map(n => n.id));
  if (newNodeIds.size > 0) {
    // Run layout on entire graph, but restore positions of existing nodes afterward
    const layoutData: ConceptCloudData = { title: local.title, nodes: mergedNodes.map(n => ({ ...n })), links: mergedLinks };
    autoLayout(layoutData);
    // Restore original positions for nodes that were already local
    for (const ln of layoutData.nodes) {
      if (!newNodeIds.has(ln.id)) {
        const orig = localNodeMap.get(ln.id);
        if (orig) { ln.x = orig.x; ln.y = orig.y; }
      }
    }
    return { ...layoutData, _version: remoteVer, _updated_by: remote._updated_by };
  }

  return { title: local.title, nodes: mergedNodes, links: mergedLinks, _version: remoteVer, _updated_by: remote._updated_by };
}

// ═══ CSS ═══

const CC_CSS = `
@keyframes ccFloatA{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
@keyframes ccFloatB{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
@keyframes ccFloatC{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
@keyframes ccPanelIn{from{opacity:0;transform:translateX(-50%) translateY(6px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}
@keyframes ccPulseOutline{0%{outline-offset:6px;outline-color:transparent}100%{outline-offset:2px;outline-color:var(--brand, #3a56e0)}}

.cc-root{display:flex;flex:1;overflow:hidden;position:relative;font-family:'Inter',-apple-system,sans-serif}

.cc-canvas-outer{flex:1;display:flex;align-items:stretch;overflow:hidden}
.cc-canvas-wrap{position:relative;width:100%;overflow:hidden}
.cc-canvas-wrap canvas{position:absolute;inset:0;z-index:0;pointer-events:none}
.cc-canvas-wrap svg.cc-link-svg{position:absolute;inset:0;z-index:1;pointer-events:none}
.cc-canvas-wrap svg.cc-link-svg .cc-link-hit{pointer-events:stroke;cursor:pointer;stroke:transparent;stroke-width:14}
.cc-canvas-wrap svg.cc-link-svg .cc-link-vis{pointer-events:none}
.cc-node-layer{position:absolute;inset:0;z-index:2}

.cc-legend{position:absolute;bottom:16px;left:16px;z-index:10;display:flex;flex-direction:column;gap:4px;font-size:var(--fs-sm, 12px);color:var(--text-muted, #888)}
.cc-legend .cc-li{display:flex;align-items:center;gap:3px}
.cc-legend .cc-ld{width:8px;height:8px;border-radius:50%}

/* MD side panel */
.cc-md-panel{flex-shrink:0;border-right:1px solid var(--border, #eee);background:var(--bg-surface, #fafbfc);display:flex;flex-direction:column;overflow:hidden}

/* Resize divider */
.cc-resize-divider{width:4px;flex-shrink:0;cursor:col-resize;background:transparent;transition:background 0.15s;z-index:20}
.cc-resize-divider:hover,.cc-resize-divider.cc-dragging{background:var(--brand, #3a56e0);opacity:0.4}
.cc-md-toolbar{height:44px;flex-shrink:0;display:flex;align-items:center;justify-content:space-between;padding:0 16px;border-bottom:1px solid var(--border, #eee);font-size:var(--fs-sm, 13px)}
.cc-md-toolbar-title{font-weight:700;color:var(--text-muted, #888);text-transform:uppercase;letter-spacing:0.08em}
.cc-md-body{flex:1;overflow-y:auto;cursor:default;padding:20px 24px;font-size:var(--fs-base, 14px);line-height:1.7;color:var(--text-secondary, #4a4a62)}
.cc-md-body h1{font-size:var(--fs-lg, 18px);font-weight:800;color:var(--text-primary, #1a1a2e);margin:0 0 14px;letter-spacing:-0.02em;padding-bottom:8px;border-bottom:1px solid var(--border, #eee)}
.cc-md-cat-header{font-size:var(--fs-sm, 13px);font-weight:700;color:var(--text-primary, #1a1a2e);margin:16px 0 6px;display:flex;align-items:center;gap:6px;text-transform:uppercase;letter-spacing:0.06em}
.cc-md-cat-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
.cc-md-node-row{padding:4px 8px;margin:1px -8px;border-radius:6px;cursor:pointer;transition:background 0.12s;display:flex;align-items:baseline;gap:6px}
.cc-md-node-row:hover{background:rgba(60,80,180,0.05)}
.cc-md-node-row.cc-active{background:rgba(60,80,180,0.1)}
.cc-md-node-row .cc-mnr-dot{width:5px;height:5px;border-radius:50%;flex-shrink:0;margin-top:4px}
.cc-md-node-row .cc-mnr-label{font-weight:600;color:var(--text-primary, #1a1a2e);font-size:var(--fs-base, 14px)}
.cc-md-node-row .cc-mnr-sub{color:var(--text-muted, #888);font-size:var(--fs-sm, 12px);margin-left:4px;font-style:italic}
.cc-md-node-row .cc-mnr-conns{font-size:var(--fs-sm, 11px);color:var(--text-muted, #888);margin-top:2px}
.cc-md-rel-table{width:100%;border-collapse:collapse;margin:8px 0;font-size:var(--fs-sm, 13px)}
.cc-md-rel-table th{text-align:left;padding:6px 8px;border-bottom:1.5px solid var(--border, #eee);font-weight:700;color:var(--text-primary);font-size:11px;text-transform:uppercase;letter-spacing:0.05em}
.cc-md-rel-table td{padding:5px 8px;border-bottom:1px solid var(--border, #eee);color:var(--text-secondary, #4a4a62)}
.cc-md-rel-table tr:hover td{background:rgba(60,80,180,0.05)}

/* Node — Style H: Gradient Text + Glow */
.cc-node{position:absolute;cursor:grab;user-select:none;transition:all 0.3s cubic-bezier(.4,0,.2,1)}
.cc-node:active{cursor:grabbing}
.cc-node.cc-dragging{z-index:200}
.cc-node:hover{z-index:100;transform:translateY(-3px)}
.cc-node.cc-selected .cc-node-inner{outline:2px solid var(--brand, #3a56e0);outline-offset:6px;border-radius:8px}
.cc-node.cc-highlight .cc-node-inner{outline:2px solid var(--brand, #3a56e0);outline-offset:4px;border-radius:8px;animation:ccPulseOutline 0.6s ease}

.cc-node-inner{display:flex;flex-direction:column;position:relative;transition:all 0.3s}
.cc-node-glow{position:absolute;width:70px;height:70px;border-radius:50%;filter:blur(35px);opacity:0.15;z-index:-1;transition:opacity 0.3s;top:0;left:-10px}
.cc-node:hover .cc-node-glow{opacity:0.3}
.cc-node-inner .cc-node-label{font-weight:800;line-height:1.05;letter-spacing:-0.04em;word-break:break-word;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;transition:font-size 0.3s}
.cc-node-inner .cc-node-sub{font-weight:500;font-size:13px;color:var(--text-muted, #999);margin-top:3px}
.cc-node-inner .cc-hover-detail{max-height:0;overflow:hidden;opacity:0;font-size:12px;color:var(--text-secondary, #666);line-height:1.5;margin-top:0;transition:all 0.3s ease}
.cc-node.cc-expanded .cc-hover-detail{max-height:80px;opacity:1;margin-top:8px}
.cc-node.cc-expanded{z-index:10}

.cc-node[data-level="1"] .cc-node-label{font-size:32px}
.cc-node[data-level="1"] .cc-node-glow{width:90px;height:90px;filter:blur(40px)}
.cc-node[data-level="2"] .cc-node-label{font-size:22px}
.cc-node[data-level="3"] .cc-node-label{font-size:16px}
.cc-node[data-level="3"] .cc-node-glow{width:50px;height:50px;filter:blur(25px)}

.cc-node .cc-port{position:absolute;width:8px;height:8px;border-radius:50%;background:rgba(79,110,255,0.5);border:1.5px solid rgba(79,110,255,0.8);bottom:-4px;left:50%;transform:translateX(-50%);cursor:crosshair;opacity:0;transition:opacity 0.2s;z-index:5}
.cc-node:hover .cc-port{opacity:1}

/* Edit button — floats near selected node */
.cc-node-edit-btn{position:absolute;z-index:50;display:none;width:28px;height:28px;padding:0;border-radius:50%;border:1px solid var(--border, rgba(255,255,255,0.15));background:var(--bg-surface, rgba(255,255,255,0.85));backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);color:var(--text-muted, #888);font-family:inherit;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,0.08);transition:all 0.15s;align-items:center;justify-content:center}
.cc-node-edit-btn.cc-neb-active{display:inline-flex;animation:ccFadeIn 0.15s ease}
.cc-node-edit-btn:hover{background:var(--bg-surface-hover, rgba(255,255,255,0.95));color:var(--brand, #3a56e0);border-color:var(--brand, #3a56e0);transform:translateY(-1px)}
@keyframes ccFadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}

/* In-place edit extras — expand below node when editing */
.cc-node.cc-editing{z-index:100}
.cc-node.cc-editing .cc-node-label{cursor:text;outline:none}
.cc-node.cc-editing .cc-node-sub{cursor:text;outline:none}
.cc-node-edit-extras{display:none;flex-direction:column;gap:4px;margin-top:6px;max-width:280px;animation:ccFadeIn 0.15s ease}
.cc-node.cc-editing .cc-node-edit-extras{display:flex}
.cc-ne-divider{height:1px;background:var(--border, #2a2a3e);margin:4px 0}
.cc-ne-notes{width:100%;border:none;outline:none;background:transparent;color:var(--text-secondary, #9ca3af);font-family:inherit;font-size:13px;line-height:1.6;padding:0;resize:none;min-height:36px}
.cc-ne-notes::placeholder{color:var(--text-muted, #4a4a5e)}
.cc-ne-actions{display:flex;gap:8px;align-items:center}
.cc-ne-actions button{padding:4px 10px;border-radius:7px;border:none;font-family:inherit;font-size:12px;font-weight:600;cursor:pointer;transition:all 0.15s}
.cc-ne-btn-save{background:var(--brand, #4d7aff);color:white}
.cc-ne-btn-save:hover{opacity:0.9}
.cc-ne-btn-cancel{background:transparent;color:var(--text-muted, #6b7280)}
.cc-ne-btn-cancel:hover{color:var(--text-primary)}
.cc-ne-btn-del{background:transparent;color:#fb7185;margin-left:auto;font-size:11px}
.cc-ne-btn-del:hover{color:#f43f5e}

/* Create overlay */
.cc-create-overlay{position:absolute;inset:0;z-index:200;background:rgba(180,180,200,0.4);backdrop-filter:blur(4px);display:none;align-items:center;justify-content:center}
.cc-create-overlay.cc-co-active{display:flex}
.cc-create-card{background:var(--bg-surface, #fff);border:1px solid var(--border-strong, rgba(0,0,0,0.16));border-radius:14px;padding:24px 28px;width:380px;max-width:90%;box-shadow:0 16px 48px rgba(0,0,0,0.08)}
.cc-create-card h2{font-size:var(--fs-md, 15px);font-weight:700;margin-bottom:3px}
.cc-cat-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px}
.cc-cat-option{display:flex;flex-direction:column;align-items:center;gap:5px;padding:14px 6px;border-radius:10px;border:1.5px solid var(--border, #eee);background:transparent;cursor:pointer;transition:all 0.2s;font-family:inherit}
.cc-cat-option:hover{border-color:var(--border-strong);background:rgba(60,80,180,0.05)}
.cc-co-dot{width:20px;height:20px;border-radius:50%}
.cc-co-label{font-size:var(--fs-2xs, 10px);font-weight:600;color:var(--text-secondary)}
.cc-cs2-field{margin-bottom:10px}
.cc-cs2-field label{display:block;font-size:var(--fs-2xs, 10px);font-weight:600;color:var(--text-muted);margin-bottom:3px}
.cc-cs2-field input,.cc-cs2-field textarea,.cc-cs2-field select{width:100%;padding:6px 10px;border-radius:7px;border:1px solid var(--border, #eee);background:transparent;color:var(--text-primary);font-family:inherit;font-size:var(--fs-base, 13px);outline:none}
.cc-cs2-field input:focus,.cc-cs2-field textarea:focus{border-color:var(--brand)}
.cc-cs2-field textarea{resize:vertical;min-height:48px;font-size:var(--fs-sm, 12px);line-height:1.5}
.cc-cs2-actions{display:flex;gap:6px;margin-top:14px}
.cc-cs2-btn{flex:1;padding:7px 14px;border-radius:7px;border:none;font-family:inherit;font-size:var(--fs-sm, 12px);font-weight:600;cursor:pointer}
.cc-cs2-btn.cc-primary{background:var(--brand, #3a56e0);color:white}
.cc-cs2-btn.cc-primary:hover{opacity:0.9}
.cc-cs2-btn.cc-ghost{background:rgba(127,127,127,0.08);color:var(--text-muted)}

/* Link tooltip */
.cc-link-tooltip{position:absolute;z-index:250;background:var(--bg-surface, #fff);border:1px solid var(--border-strong);border-radius:8px;padding:6px 8px;display:none;box-shadow:0 6px 24px rgba(0,0,0,0.08)}
.cc-link-tooltip.cc-lt-active{display:flex;gap:5px;align-items:center}
.cc-link-tooltip .cc-lt-label{font-size:var(--fs-2xs, 10px);color:var(--text-muted);white-space:nowrap}
.cc-link-tooltip input{width:90px;padding:3px 6px;border-radius:5px;border:1px solid var(--border, #eee);background:transparent;color:var(--text-primary);font-family:inherit;font-size:var(--fs-2xs, 10px);outline:none}
.cc-link-tooltip input:focus{border-color:var(--brand)}
.cc-link-tooltip .cc-lt-del{width:20px;height:20px;border-radius:5px;border:none;background:rgba(251,113,133,0.1);color:#fb7185;cursor:pointer;font-size:11px;display:flex;align-items:center;justify-content:center}

/* Header bar inside concept cloud */
.cc-hdr{height:44px;flex-shrink:0;display:flex;align-items:center;justify-content:space-between;padding:0 16px;border-bottom:1px solid var(--border, #eee);background:var(--bg-surface, #fff);font-size:var(--fs-base, 14px)}
.cc-hdr-right{display:flex;align-items:center;gap:6px}
.cc-btn{padding:6px 14px;border-radius:8px;border:1px solid var(--border, #eee);background:transparent;color:var(--text-muted);font-family:inherit;font-size:var(--fs-sm, 13px);font-weight:600;cursor:pointer;display:flex;align-items:center;gap:5px;transition:all 0.15s}
.cc-btn:hover{border-color:var(--border-strong);color:var(--text-primary);background:rgba(60,80,180,0.05)}
.cc-btn.cc-brand{background:var(--brand, #3a56e0);border-color:var(--brand, #3a56e0);color:white}
.cc-btn.cc-brand:hover{opacity:0.9}
`;

// ═══ Component ═══

export default function ConceptCloud({
  deckId, deckTitle, audienceInfo, goalInfo, keyMessages, onClose, contextVersion,
  prefetchedContext, contextHydratedDeckId,
}: ConceptCloudProps) {
  // Initialize with derived data immediately (no loading state for empty decks)
  const [data, setData] = useState<ConceptCloudData | null>(() =>
    deriveFromProps(deckTitle || 'Pitch Brief', audienceInfo, goalInfo, keyMessages)
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState<1 | 2>(1);
  const [createCat, setCreateCat] = useState<string | null>(null);
  const [linkEditIdx, setLinkEditIdx] = useState<number | null>(null);
  const [mdPanelWidth, setMdPanelWidth] = useState(320);
  const [linkTooltipPos, setLinkTooltipPos] = useState({ x: 0, y: 0 });
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'graph' | 'markdown'>('graph');

  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const gridCanvasRef = useRef<HTMLCanvasElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the deckId we initialized data for. When the user opens a
  // different deck the component stays mounted but receives a new
  // deckId — comparing against this ref lets the load-from-context
  // effect re-arm correctly. (Previously a plain boolean ref blocked
  // re-init across deck switches.)
  const dataLoadedDeckIdRef = useRef<string | null>(null);
  // Kept for compatibility with downstream blocks that gate on
  // 'have we ever loaded'. Mirrors dataLoadedDeckIdRef !== null.
  const dataLoadedRef = useRef(false);
  const draggedRef = useRef(false);

  // Create form refs
  const createLabelRef = useRef<HTMLInputElement>(null);
  const createSubRef = useRef<HTMLInputElement>(null);
  const createLevelRef = useRef<HTMLSelectElement>(null);
  const createNotesRef = useRef<HTMLTextAreaElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);

  // Edit form refs
  const editLabelRef = useRef<HTMLInputElement>(null);
  const editSubRef = useRef<HTMLInputElement>(null);
  const editNotesRef = useRef<HTMLTextAreaElement>(null);

  const CW = useCallback(() => canvasWrapRef.current?.clientWidth || 800, []);
  const CH = useCallback(() => canvasWrapRef.current?.clientHeight || 600, []);

  // ── Load from context or derive from props ──
  // Parent-managed mode (prefetchedContext defined): wait for
  // contextHydratedDeckId to match this deckId, then use the prop
  // snapshot — no listContext fetch. Standalone mode (prop undefined):
  // legacy fetch path.
  //
  // dataLoadedRef is keyed by deckId via the dependency list — we
  // gate on it so we don't repeatedly process the same prefetched
  // snapshot, but a deckId change naturally re-arms it.
  useEffect(() => {
    if (!deckId) return;

    // Parent is managing context but hasn't finished loading yet. Wait
    // — once contextHydratedDeckId flips to deckId, this effect re-runs
    // (it's a dep) and the path below executes.
    const parentManaged = prefetchedContext !== undefined;
    if (parentManaged && contextHydratedDeckId !== deckId) {
      return;
    }

    // Re-arm on deck switch: only short-circuit if we already loaded
    // for THIS deck id, not just "loaded ever".
    if (dataLoadedDeckIdRef.current === deckId) return;
    dataLoadedDeckIdRef.current = deckId;
    dataLoadedRef.current = true;

    if (parentManaged) {
      const brief = prefetchedContext!.find(c => c.key === 'memory/pitch-brief.json' || c.key === 'memory/pitch-brief');
      if (brief?.content) {
        try {
          const parsed = JSON.parse(brief.content) as ConceptCloudData;
          if (parsed.nodes?.length) {
            setData(mergeGatherData(parsed, audienceInfo, goalInfo, keyMessages));
            return;
          }
        } catch {}
      }
      // Fresh deck OR no saved brief — derive from props.
      setData(deriveFromProps(deckTitle || 'Pitch Brief', audienceInfo, goalInfo, keyMessages));
      return;
    }

    // Standalone mode (no parent prefetch).
    (async () => {
      try {
        const ctx = await api.listContext(deckId);
        const brief = ctx.find(c => c.key === 'memory/pitch-brief.json' || c.key === 'memory/pitch-brief');
        if (brief?.content) {
          const parsed = JSON.parse(brief.content) as ConceptCloudData;
          if (parsed.nodes?.length) {
            const merged = mergeGatherData(parsed, audienceInfo, goalInfo, keyMessages);
            setData(merged);
            return;
          }
        }
      } catch {}
      const derived = deriveFromProps(deckTitle || 'Pitch Brief', audienceInfo, goalInfo, keyMessages);
      setData(derived);
    })();
  }, [deckId, contextHydratedDeckId]);

  // ── Merge new gather data when props update ──
  useEffect(() => {
    if (!data || !dataLoadedRef.current) return;
    const merged = mergeGatherData(data, audienceInfo, goalInfo, keyMessages);
    if (merged !== data) setData(merged);
  }, [audienceInfo, goalInfo, keyMessages]);

  // ── Reload from context when contextVersion changes (agent wrote new data) ──
  useEffect(() => {
    if (!deckId || !contextVersion || !dataLoadedRef.current) return;
    (async () => {
      try {
        const ctx = await api.listContext(deckId);
        const brief = ctx.find((c: { key: string }) => c.key === 'memory/pitch-brief.json' || c.key === 'memory/pitch-brief');
        if (!brief?.content) return;
        const remote = JSON.parse(brief.content) as ConceptCloudData;
        if (!remote.nodes?.length) return;
        setData(prev => prev ? mergeRemoteData(prev, remote) : remote);
      } catch {}
    })();
  }, [contextVersion, deckId]);

  // ── Debounced save to context ──
  const saveToContext = useCallback((d: ConceptCloudData) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const toSave = { ...d, _version: (d._version ?? 0) + 1, _updated_by: 'user' };
      api.saveContext(deckId, 'memory/pitch-brief.json', JSON.stringify(toSave)).catch(() => {});
    }, 800);
  }, [deckId]);

  // ── Update data and trigger save ──
  const updateData = useCallback((updater: (prev: ConceptCloudData) => ConceptCloudData) => {
    setData(prev => {
      if (!prev) return prev;
      const next = updater(prev);
      saveToContext(next);
      return next;
    });
  }, [saveToContext]);

  // ── Draw grid ──
  const drawGrid = useCallback(() => {
    const gc = gridCanvasRef.current;
    const wrap = canvasWrapRef.current;
    if (!gc || !wrap) return;
    const w = wrap.clientWidth, h = wrap.clientHeight;
    gc.width = w; gc.height = h;
    const gx = gc.getContext('2d');
    if (!gx) return;
    gx.clearRect(0, 0, w, h);
    gx.strokeStyle = 'rgba(0,0,0,0.04)';
    gx.lineWidth = 0.5;
    for (let x = 40; x < w; x += 40) { gx.beginPath(); gx.moveTo(x, 0); gx.lineTo(x, h); gx.stroke(); }
    for (let y = 40; y < h; y += 40) { gx.beginPath(); gx.moveTo(0, y); gx.lineTo(w, y); gx.stroke(); }
  }, []);

  useEffect(() => {
    drawGrid();
    const onResize = () => drawGrid();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [drawGrid]);

  // ── Node drag ──
  const startDrag = useCallback((n: ConceptNode, e: React.PointerEvent) => {
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, ox = n.x, oy = n.y;
    let moved = false;
    draggedRef.current = false;

    const onM = (ev: PointerEvent) => {
      const dx = (ev.clientX - sx) / CW(), dy = (ev.clientY - sy) / CH();
      if (Math.abs(dx) > 0.004 || Math.abs(dy) > 0.004) moved = true;
      updateData(prev => ({
        ...prev,
        nodes: prev.nodes.map(nd =>
          nd.id === n.id ? { ...nd, x: Math.max(0.05, Math.min(0.95, ox + dx)), y: Math.max(0.06, Math.min(0.92, oy + dy)) } : nd
        ),
      }));
    };
    const onU = () => {
      document.removeEventListener('pointermove', onM);
      document.removeEventListener('pointerup', onU);
      if (moved) draggedRef.current = true;
    };
    document.addEventListener('pointermove', onM);
    document.addEventListener('pointerup', onU);
  }, [CW, CH, updateData]);

  // ── Link drag from port ──
  const startLinkDrag = useCallback((fromId: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!data) return;

    const fromNode = data.nodes.find(nd => nd.id === fromId);
    if (!fromNode) return;

    // Create temporary SVG line
    const wrap = canvasWrapRef.current;
    if (!wrap) return;
    const svg = wrap.querySelector('svg.cc-link-svg');
    if (!svg) return;
    const rect = wrap.getBoundingClientRect();

    const tmpLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    tmpLine.setAttribute('x1', String(fromNode.x * CW()));
    tmpLine.setAttribute('y1', String(fromNode.y * CH()));
    tmpLine.setAttribute('x2', String(e.clientX - rect.left));
    tmpLine.setAttribute('y2', String(e.clientY - rect.top));
    tmpLine.setAttribute('stroke', 'rgba(79,110,255,0.6)');
    tmpLine.setAttribute('stroke-width', '2');
    tmpLine.setAttribute('stroke-dasharray', '6,3');
    tmpLine.style.pointerEvents = 'none';
    svg.appendChild(tmpLine);

    const onM = (ev: PointerEvent) => {
      tmpLine.setAttribute('x2', String(ev.clientX - rect.left));
      tmpLine.setAttribute('y2', String(ev.clientY - rect.top));
    };
    const onU = (ev: PointerEvent) => {
      document.removeEventListener('pointermove', onM);
      document.removeEventListener('pointerup', onU);
      tmpLine.remove();

      const target = document.elementFromPoint(ev.clientX, ev.clientY)?.closest('.cc-node') as HTMLElement | null;
      if (target && target.dataset.id && target.dataset.id !== fromId) {
        const toId = target.dataset.id;
        updateData(prev => {
          if (prev.links.some(l => (l.from === fromId && l.to === toId) || (l.from === toId && l.to === fromId))) return prev;
          return { ...prev, links: [...prev.links, { from: fromId, to: toId, relation: '' }] };
        });
      }
    };
    document.addEventListener('pointermove', onM);
    document.addEventListener('pointerup', onU);
  }, [data, CW, CH, updateData]);

  // ── Select node ──
  const selectNode = useCallback((id: string | null) => {
    setLinkEditIdx(null);
    setSelectedNodeId(id);
    setShowEditPanel(false);
    setEditMode(false);
  }, []);

  // ── Delete selected node ──
  const deleteNode = useCallback(() => {
    if (!selectedNodeId) return;
    updateData(prev => ({
      ...prev,
      nodes: prev.nodes.filter(n => n.id !== selectedNodeId),
      links: prev.links.filter(l => l.from !== selectedNodeId && l.to !== selectedNodeId),
    }));
    setSelectedNodeId(null);
    setEditMode(false);
  }, [selectedNodeId, updateData]);

  // ── Save detail edit ──
  const saveDetailEdit = useCallback(() => {
    if (!selectedNodeId) return;
    // Read from contentEditable (textContent) or input (value)
    const labelEl = editLabelRef.current as any;
    const subEl = editSubRef.current as any;
    const notesEl = editNotesRef.current as any;
    const label = (labelEl?.textContent || labelEl?.value || '').trim();
    if (!label) return;
    updateData(prev => ({
      ...prev,
      nodes: prev.nodes.map(n =>
        n.id === selectedNodeId ? {
          ...n,
          label: label.length > 16 ? label.replace(/\s+/g, '\n') : label,
          sub: (subEl?.textContent || subEl?.value || '').trim(),
          notes: (notesEl?.textContent || notesEl?.value || '').trim(),
        } : n
      ),
    }));
    setEditMode(false);
  }, [selectedNodeId, updateData]);

  // ── Create concept ──
  const createFinish = useCallback(() => {
    const label = createLabelRef.current?.value.trim();
    if (!label || !createCat) return;
    const id = genId();
    const level = parseInt(createLevelRef.current?.value || '2');
    updateData(prev => ({
      ...prev,
      nodes: [...prev.nodes, {
        id,
        label: label.length > 16 ? label.replace(/\s+/g, '\n') : label,
        sub: createSubRef.current?.value.trim() || '',
        cat: createCat,
        level,
        x: 0.15 + Math.random() * 0.7,
        y: 0.15 + Math.random() * 0.65,
        notes: createNotesRef.current?.value.trim() || '',
      }],
    }));
    setCreateOpen(false);
    setCreateStep(1);
    setCreateCat(null);
    selectNode(id);
  }, [createCat, updateData, selectNode]);

  // ── Link edit ──
  const openLinkEdit = useCallback((idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNodeId(null);
    setLinkEditIdx(idx);
    setLinkTooltipPos({ x: e.clientX, y: e.clientY - 36 });
  }, []);

  const saveLinkEdit = useCallback(() => {
    if (linkEditIdx === null || !data) return;
    const val = linkInputRef.current?.value.trim() || '';
    updateData(prev => ({
      ...prev,
      links: prev.links.map((l, i) => i === linkEditIdx ? { ...l, relation: val } : l),
    }));
    setLinkEditIdx(null);
  }, [linkEditIdx, data, updateData]);

  const deleteLink = useCallback(() => {
    if (linkEditIdx === null) return;
    updateData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== linkEditIdx),
    }));
    setLinkEditIdx(null);
  }, [linkEditIdx, updateData]);

  // ── Auto layout ──
  const doAutoLayout = useCallback(() => {
    updateData(prev => {
      const copy = { ...prev, nodes: prev.nodes.map(n => ({ ...n })) };
      autoLayout(copy);
      return copy;
    });
  }, [updateData]);

  // ── Click MD node → highlight on canvas ──
  const mdClickNode = useCallback((id: string) => {
    selectNode(id);
    setHighlightId(id);
    setTimeout(() => setHighlightId(null), 800);
  }, [selectNode]);

  // ── Click away handler ──
  // ── MD panel resize ──
  const handleMdResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = mdPanelWidth;
    const divider = e.currentTarget as HTMLElement;
    divider.classList.add('cc-dragging');
    const onMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      setMdPanelWidth(Math.max(200, Math.min(500, startW + delta)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      divider.classList.remove('cc-dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [mdPanelWidth]);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.cc-node') && !target.closest('.cc-detail-panel') && !target.closest('.cc-link-tooltip')) {
      setSelectedNodeId(null);
      setEditMode(false);
      setLinkEditIdx(null);
    }
  }, []);

  if (!data) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
        Loading concept cloud...
      </div>
    );
  }

  if (data.nodes.length === 0 && data.links.length === 0) {
    return null;
  }

  const selectedNode = selectedNodeId ? data.nodes.find(n => n.id === selectedNodeId) : null;
  const selectedCC = selectedNode ? (CAT[selectedNode.cat] || CAT.context) : null;

  const catOrder = ['goal', 'audience', 'message', 'differ', 'pain', 'context'];
  const groups: Record<string, ConceptNode[]> = {};
  data.nodes.forEach(n => { if (!groups[n.cat]) groups[n.cat] = []; groups[n.cat].push(n); });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{CC_CSS}</style>

      {/* Header */}
      <div className="cc-hdr">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, letterSpacing: '-0.01em' }}>{data.title}</span>
          <span style={{ fontSize: 'var(--fs-2xs, 10px)', color: 'var(--text-muted)', marginLeft: 10 }}>
            {data.nodes.length} concepts · {data.links.length} connections
          </span>
        </div>
      </div>

      {/* Main: toggled view — graph or markdown */}
      <div className="cc-root">
        {viewMode === 'markdown' ? (
        /* Full-width markdown view */
        <div className="cc-md-panel" style={{ width: '100%', flex: 1 }}>
          <div className="cc-md-toolbar">
            <span className="cc-md-toolbar-title">Pitch Brief</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="cc-btn" style={{ fontSize: 9, padding: '2px 7px' }}
                onClick={() => {
                  const md = toMarkdownText(data);
                  navigator.clipboard.writeText(md).catch(() => {});
                }}>
                Copy
              </button>
              <button className="cc-btn" onClick={() => setViewMode('graph')}>
                🔮 Graph
              </button>
            </div>
          </div>
          <div className="cc-md-body">
            <h1>{data.title}</h1>
            {catOrder.map(cat => {
              const nodes = groups[cat];
              if (!nodes?.length) return null;
              const cc = CAT[cat];
              const sorted = [...nodes].sort((a, b) => a.level - b.level);
              return (
                <div key={cat}>
                  <div className="cc-md-cat-header">
                    <div className="cc-md-cat-dot" style={{ background: cc.c }} />
                    {cc.icon} {cc.label}
                  </div>
                  {sorted.map(n => {
                    const label = n.label.replace(/\n/g, ' ');
                    const isActive = selectedNodeId === n.id;
                    const conns = data.links
                      .filter(l => l.from === n.id || l.to === n.id)
                      .map(l => {
                        const o = data.nodes.find(nd => nd.id === (l.from === n.id ? l.to : l.from));
                        return o ? { name: o.label.replace(/\n/g, ' '), rel: l.relation } : null;
                      }).filter(Boolean) as { name: string; rel: string }[];
                    const connStr = conns.map(c => c.name + (c.rel ? ` (${c.rel})` : '')).join(', ');

                    return (
                      <div key={n.id} className={`cc-md-node-row${isActive ? ' cc-active' : ''}`}
                        onClick={() => mdClickNode(n.id)}>
                        <div className="cc-mnr-dot" style={{ background: cc.c, alignSelf: 'flex-start' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span className="cc-mnr-label">{label}</span>
                          {n.sub && <span className="cc-mnr-sub">{n.sub}</span>}
                          {connStr && <div className="cc-mnr-conns">→ {connStr}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Relationship table */}
            {data.links.length > 0 && (
              <div style={{ marginTop: 14, paddingTop: 8, borderTop: '1px solid var(--border, #eee)' }}>
                <div className="cc-md-cat-header" style={{ marginTop: 0 }}>Relationships</div>
                <table className="cc-md-rel-table">
                  <thead>
                    <tr><th>From</th><th>To</th><th>Relation</th></tr>
                  </thead>
                  <tbody>
                    {data.links.map((l, i) => {
                      const f = data.nodes.find(n => n.id === l.from);
                      const t = data.nodes.find(n => n.id === l.to);
                      if (!f || !t) return null;
                      return (
                        <tr key={i}>
                          <td>{f.label.replace(/\n/g, ' ')}</td>
                          <td>{t.label.replace(/\n/g, ' ')}</td>
                          <td>{l.relation || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        ) : (
        /* Full-width graph view */
        <div className="cc-canvas-outer" style={{ position: 'relative' }}>
        {/* Floating toolbar */}
        <div style={{
          position: 'absolute', top: 10, right: 10, zIndex: 20,
          display: 'flex', gap: 6, alignItems: 'center',
        }}>
          <button className="cc-btn" onClick={doAutoLayout}>↕ Layout</button>
        </div>
        <div className="cc-canvas-wrap" ref={canvasWrapRef} onClick={handleCanvasClick}>
          <canvas ref={gridCanvasRef} />
          {/* SVG links */}
          <svg className="cc-link-svg" width={CW()} height={CH()}>
            {data.links.map((lk, i) => {
              const f = data.nodes.find(n => n.id === lk.from);
              const t = data.nodes.find(n => n.id === lk.to);
              if (!f || !t) return null;
              const x1 = f.x * CW(), y1 = f.y * CH(), x2 = t.x * CW(), y2 = t.y * CH();
              return (
                <React.Fragment key={`link-${i}`}>
                  <line className="cc-link-vis" x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke="rgba(60,80,180,0.2)" strokeWidth="1.2" strokeDasharray="5,4" />
                  <line className="cc-link-hit" x1={x1} y1={y1} x2={x2} y2={y2}
                    onClick={(e) => openLinkEdit(i, e)} />
                  {lk.relation && (
                    <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 4} textAnchor="middle"
                      fill="rgba(60,80,180,0.45)" fontSize="8" fontFamily="Inter,sans-serif" fontWeight="500"
                      style={{ pointerEvents: 'none' }}>
                      {lk.relation}
                    </text>
                  )}
                </React.Fragment>
              );
            })}
          </svg>

          {/* Node layer */}
          <div className="cc-node-layer">
            {data.nodes.map(n => {
              const size = nodeSize(n.level);
              const cc = CAT[n.cat] || CAT.context;
              const h = n.id.charCodeAt(n.id.length - 1);
              const fa = FLOAT_ANIMS[h % 3];
              const fd = ((h % 7) * 0.5);
              const gs = Math.round(size * 0.12);
              const conns = data.links.filter(l => l.from === n.id || l.to === n.id)
                .map(l => { const o = data.nodes.find(nd => nd.id === (l.from === n.id ? l.to : l.from)); return o ? o.label.replace(/\n/g, ' ') : null; })
                .filter(Boolean);

              const isSelected = selectedNodeId === n.id;
              const isHighlight = highlightId === n.id;
              const isEditing = isSelected && showEditPanel;
              const className = `cc-node${isSelected ? ' cc-selected' : ''}${isHighlight ? ' cc-highlight' : ''}${isEditing ? ' cc-editing' : ''}`;

              return (
                <div key={n.id} className={className} data-id={n.id} data-level={n.level}
                  style={{ left: n.x * CW() - size * 0.4, top: n.y * CH() - size * 0.2 }}
                  onPointerDown={(e) => {
                    if ((e.target as HTMLElement).classList.contains('cc-port')) return;
                    if (isEditing) return; // don't drag while editing
                    startDrag(n, e);
                  }}
                  onClick={(e) => {
                    if (isEditing) return; // don't re-select while editing
                    if (draggedRef.current) { draggedRef.current = false; return; }
                    if ((e.target as HTMLElement).classList.contains('cc-port')) return;
                    selectNode(n.id);
                  }}
                >
                  <div className="cc-node-inner" style={{
                    animation: fa,
                    animationDelay: `${fd}s`,
                  }}>
                    <div className="cc-node-glow" style={{ background: cc.c }} />
                    <div className="cc-node-label"
                      ref={isEditing ? editLabelRef as any : undefined}
                      contentEditable={isEditing}
                      suppressContentEditableWarning
                      style={{
                        backgroundImage: `linear-gradient(135deg, ${cc.c}, ${cc.c}90)`,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {n.label}
                    </div>
                    <div className="cc-node-sub"
                      ref={isEditing ? editSubRef as any : undefined}
                      contentEditable={isEditing}
                      suppressContentEditableWarning
                    >
                      {n.sub || (isEditing ? '' : '')}
                    </div>
                    {!isEditing && n.notes && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)', marginTop: 4, lineHeight: 1.4, maxWidth: 200, opacity: 0.7 }}>
                        {n.notes.length > 60 ? n.notes.slice(0, 60) + '...' : n.notes}
                      </div>
                    )}
                  </div>
                  {/* Edit extras: notes + actions, shown below node when editing */}
                  <div className="cc-node-edit-extras" onClick={e => e.stopPropagation()}>
                    <div className="cc-ne-divider" />
                    <textarea ref={isEditing ? editNotesRef as any : undefined}
                      className="cc-ne-notes"
                      key={isEditing ? `notes-edit-${n.id}` : `notes-${n.id}`}
                      defaultValue={n.notes || ''}
                      placeholder="Add notes..." />
                    <div className="cc-ne-actions">
                      <button className="cc-ne-btn-del" onClick={() => { deleteNode(); setShowEditPanel(false); }}>Delete</button>
                      <button className="cc-ne-btn-cancel" onClick={() => { setShowEditPanel(false); selectNode(null); }}>Cancel</button>
                      <button className="cc-ne-btn-save" onClick={() => { saveDetailEdit(); setShowEditPanel(false); }}>Save</button>
                    </div>
                  </div>
                  <div className="cc-port" data-node={n.id}
                    onPointerDown={(e) => startLinkDrag(n.id, e)} />
                </div>
              );
            })}
          </div>

          {/* Legend — only show when there are nodes */}
          {data.nodes.length > 0 && (
            <div className="cc-legend">
              {Object.entries(CAT).map(([k, v]) => (
                <div key={k} className="cc-li">
                  <div className="cc-ld" style={{ background: v.c }} />
                  {v.label}
                </div>
              ))}
            </div>
          )}

          {/* Floating edit icon — pinned to top-right corner of the selected node */}
          {selectedNode && !showEditPanel && (() => {
            const nodeEl = canvasWrapRef.current?.querySelector(`[data-id="${selectedNode.id}"]`) as HTMLElement;
            if (!nodeEl) return null;
            const btnX = nodeEl.offsetLeft + nodeEl.offsetWidth + 4;
            const btnY = nodeEl.offsetTop - 6;
            return (
              <button
                className="cc-node-edit-btn cc-neb-active"
                style={{ left: btnX, top: btnY }}
                onClick={(e) => { e.stopPropagation(); setShowEditPanel(true); setEditMode(true); }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="8" cy="8" r="1.2"/><circle cx="8" cy="3" r="1.2"/><circle cx="8" cy="13" r="1.2"/>
                </svg>
              </button>
            );
          })()}

          {/* In-place editing is now handled directly in the node rendering above */}

          {/* Link tooltip */}
          <div className={`cc-link-tooltip${linkEditIdx !== null ? ' cc-lt-active' : ''}`}
            style={{ left: linkTooltipPos.x, top: linkTooltipPos.y }}>
            <span className="cc-lt-label">Relation:</span>
            <input ref={linkInputRef}
              defaultValue={linkEditIdx !== null ? data.links[linkEditIdx]?.relation || '' : ''}
              key={linkEditIdx}
              placeholder="e.g. targets"
              onBlur={saveLinkEdit}
              onKeyDown={(e) => { if (e.key === 'Enter') saveLinkEdit(); }}
              autoFocus={linkEditIdx !== null}
            />
            <button className="cc-lt-del" onClick={deleteLink} title="Remove">&times;</button>
          </div>

          {/* Create overlay */}
          <div className={`cc-create-overlay${createOpen ? ' cc-co-active' : ''}`}
            onClick={(e) => { if (e.target === e.currentTarget) { setCreateOpen(false); setCreateStep(1); } }}>
            <div className="cc-create-card">
              {createStep === 1 ? (
                <>
                  <h2>Add a Concept</h2>
                  <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginBottom: 16 }}>What type of concept?</div>
                  <div className="cc-cat-grid">
                    {Object.entries(CAT).map(([k, v]) => (
                      <button key={k} className="cc-cat-option" onClick={() => { setCreateCat(k); setCreateStep(2); }}>
                        <div className="cc-co-dot" style={{ background: v.c }} />
                        <div className="cc-co-label">{v.icon} {v.label}</div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {createCat && CAT[createCat] && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 6, fontSize: 'var(--fs-2xs)',
                      fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' as const,
                      marginBottom: 12, background: CAT[createCat].c + '15', color: CAT[createCat].c,
                    }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: CAT[createCat].c }} />
                      {CAT[createCat].label}
                    </div>
                  )}
                  <div className="cc-cs2-field">
                    <label>Label</label>
                    <input ref={createLabelRef} placeholder="e.g. Enterprise CTO" autoFocus />
                  </div>
                  <div className="cc-cs2-field">
                    <label>Subtitle</label>
                    <input ref={createSubRef} placeholder="Optional" />
                  </div>
                  <div className="cc-cs2-field">
                    <label>Level</label>
                    <select ref={createLevelRef} defaultValue="2">
                      <option value="1">Level 1 — Primary</option>
                      <option value="2">Level 2 — Secondary</option>
                      <option value="3">Level 3 — Detail</option>
                    </select>
                  </div>
                  <div className="cc-cs2-field">
                    <label>Notes</label>
                    <textarea ref={createNotesRef} placeholder="Additional context..." />
                  </div>
                  <div className="cc-cs2-actions">
                    <button className="cc-cs2-btn cc-ghost" onClick={() => { setCreateStep(1); setCreateCat(null); }}>Back</button>
                    <button className="cc-cs2-btn cc-primary" onClick={createFinish}>Add</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        </div>
        )}
      </div>
    </div>
  );
}

// ═══ Markdown export ═══

function toMarkdownText(data: ConceptCloudData): string {
  const lines = [`# ${data.title}`, ''];
  const catOrder = ['goal', 'audience', 'message', 'differ', 'pain', 'context'];
  const groups: Record<string, ConceptNode[]> = {};
  data.nodes.forEach(n => { if (!groups[n.cat]) groups[n.cat] = []; groups[n.cat].push(n); });

  for (const cat of catOrder) {
    const nodes = groups[cat];
    if (!nodes?.length) continue;
    const cc = CAT[cat];
    nodes.sort((a, b) => a.level - b.level);
    lines.push(`## ${cc.icon} ${cc.label}`, '');
    for (const n of nodes) {
      const l = n.label.replace(/\n/g, ' ');
      const p = n.level <= 2 ? (n.level === 1 ? '###' : '####') + ' ' + l : `- **${l}**`;
      lines.push(p);
      if (n.sub) lines.push(`  - _${n.sub}_`);
      if (n.notes) lines.push(`  - ${n.notes}`);
      const conns = data.links
        .filter(lk => lk.from === n.id || lk.to === n.id)
        .map(lk => {
          const o = data.nodes.find(nd => nd.id === (lk.from === n.id ? lk.to : lk.from));
          return o ? (o.label.replace(/\n/g, ' ') + (lk.relation ? ` _(${lk.relation})_` : '')) : null;
        }).filter(Boolean);
      if (conns.length) lines.push(`  - Connections: ${conns.join(', ')}`);
      lines.push('');
    }
  }

  if (data.links.length) {
    lines.push('## Relationships', '', '| From | To | Relation |', '|------|----|----------|');
    for (const l of data.links) {
      const f = data.nodes.find(n => n.id === l.from);
      const t = data.nodes.find(n => n.id === l.to);
      if (f && t) lines.push(`| ${f.label.replace(/\n/g, ' ')} | ${t.label.replace(/\n/g, ' ')} | ${l.relation || '—'} |`);
    }
    lines.push('');
  }

  return lines.join('\n');
}
